<template>
    <div class="line-with-area-chart">
        <div
            ref="graphicParent"
            class="vis-wrapper"
        >
            <div
                :id="displayDiv"
                ref="vis"
                class="chart-display-div"
            />
        </div>
    </div>
</template>
<script>
import vegaChartHelpers from '@foundryspatial/mixin-vega-chart-helpers';

import scssVars from '@/assets/scss/_variables.scss';

export default {
    mixins: [
        vegaChartHelpers,
    ],
    props: {
        /**
         * The data is expected to be in the following format:
         *  [
         *      {
         *          x: 0,
         *          y: 1054,
         *          min: 433,
         *          max: 2300,
         *      },
         *      {
         *          x: 1,
         *          y: 952,
         *          min: 420,
         *          max: 2259
         *      },
         *      [...]
         *  ]
         */
        graphData: {
            type: Array,
            required: true,
        },
        /**
         * The following options can be provided:
         *
         * - xAxisLabel                (string)
         *      -> the label for the x axis
         *
         * - yAxisLabel                (string)
         *      -> the label for the y axis
         *
         * - yUnitOfMeasurement        (string)
         *      -> the unit of measurement for the y axis
         *
         * - xUnitOfMeasurement        (string)
         *      -> the unit of measurement for the x axis
         *
         * - roundToDecimalPlaces      (number)
         *      -> the number of decimal places to round values to
         *
         * - lineColor                 (string)
         *      -> the color for the chart line
         *
         * - tooltipSignal             (string)
         *      -> a custom tooltip signal. Must follow vega-tooltip specifications
         */
        providedOptions: {
            type: Object,
            required: false,
            default: () => ({}),
        },

        /**
         * The height in pixels the chart will be. If this is not provided, the chart height will be responsive and can
         * be set with styling.
         */
        height: {
            type: Number,
            required: false,
            default: null,
        },
    },

    data: () => ({
        tooltipData: [],
        vegaView: null,
        vegaSpec: {
            $schema: 'https://vega.github.io/schema/vega/v5.json',
            config: {
                axis: {
                    grid: true,
                },
            },
            padding: 1,
            autosize: { type: 'fit', contains: 'padding' },
            data: [
                {
                    name: 'chart',
                    values: [],
                },
                {
                    name: 'tooltipData',
                    values: [],
                },
            ],
            signals: [
                {
                    name: 'width',
                    update: 'containerSize()[0]',
                    on: [
                        {
                            events: { source: 'window', type: 'resize', throttle: 50 },
                            update: 'containerSize()[0]',
                        },
                    ],
                },
                {
                    name: 'height',
                    update: 'containerSize()[1]',
                    on: [
                        {
                            events: { source: 'window', type: 'resize', throttle: 50 },
                            update: 'containerSize()[1]',
                        },
                    ],
                },
            ],
            scales: [
                {
                    name: 'x',
                    type: 'linear',
                    range: 'width',
                    domain: { data: 'chart', field: 'x' },
                    zero: false,
                },
                {
                    name: 'y',
                    type: 'linear',
                    range: 'height',
                    nice: true,
                    domain: {
                        fields: [
                            { data: 'chart', field: 'y' },
                            { data: 'chart', field: 'min' },
                            { data: 'chart', field: 'max' },
                        ],
                    },
                },
            ],
            axes: [
                {
                    orient: 'bottom',
                    scale: 'x',
                    titlePadding: 10,
                    grid: false,
                    zindex: 2,
                },
                {
                    orient: 'left',
                    scale: 'y',
                    titlePadding: 10,
                },
            ],
            marks: [
                {
                    type: 'area',
                    from: { data: 'chart' },
                    encode: {
                        update: {
                            x: { scale: 'x', field: 'x' },
                            y: { scale: 'y', field: 'min' },
                            y2: { scale: 'y', field: 'max' },
                            interpolate: { value: 'monotone' },
                            fill: { value: scssVars.colorTopographyChartArea },
                            tooltip: { },
                        },
                    },
                },
                {
                    type: 'line',
                    from: { data: 'chart' },
                    encode: {
                        enter: {
                            x: { scale: 'x', field: 'x' },
                            y: { scale: 'y', field: 'min' },
                            interpolate: { value: 'monotone' },
                            stroke: { value: scssVars.colorTopographyChartMinMax },
                        },
                    },
                },
                {
                    type: 'line',
                    from: { data: 'chart' },
                    encode: {
                        enter: {
                            x: { scale: 'x', field: 'x' },
                            y: { scale: 'y', field: 'max' },
                            interpolate: { value: 'monotone' },
                            stroke: { value: scssVars.colorTopographyChartMinMax },
                        },
                    },
                },
                {
                    type: 'line',
                    from: { data: 'chart' },
                    encode: {
                        enter: {
                            x: { scale: 'x', field: 'x' },
                            y: { scale: 'y', field: 'y' },
                            interpolate: { value: 'monotone' },
                            stroke: {},
                        },
                    },
                },
                {
                    type: 'area',
                    from: { data: 'tooltipData' },
                    encode: {
                        enter: {
                            x: { scale: 'x', field: 'x' },
                            height: { field: { group: 'height' } },
                            stroke: { value: 'transparent' },
                            fill: { value: 'transparent' },
                            tooltip: { },
                        },
                    },
                },
            ],
        },
    }),

    computed: {
        options() {
            return {
                xAxisLabel: '',
                yAxisLabel: '',
                yUnitOfMeasurement: '',
                xUnitOfMeasurement: '',
                roundToDecimalPlaces: null,
                lineColor: '#000',
                tooltipSignal: '',
                hideTooltip: false,
                ...this.providedOptions,
            };
        },
        tooltipSignal() {
            return `
                {
                    title: datum.x + "${this.options.xUnitOfMeasurement}",
                    "${this.options.yAxisLabel}": datum.y + "${this.options.yUnitOfMeasurement}"
                }
            `;
        },
    },

    watch: {
        graphData() {
            this.handleDataChange();
        },
        providedOptions() {
            this.handleOptionChange();
        },
    },

    mounted() {
        if (this.height) {
            this.vegaSpec.height = this.height;
            this.vegaSpec.signals.splice(1, 1);
        }
        this.handleDataChange();
        this.handleOptionChange();
    },

    methods: {
        /**
         * handle the data or any options changing
         */
        handleDataChange() {
            this.prepareDataForChart();
            this.render();
        },

        /**
         * Deal with any changing options passed in as props and re-render the chart.
         */
        handleOptionChange() {
            this.vegaSpec.axes[0].title = `${this.options.xAxisLabel} ${this.options.xUnitOfMeasurement}`;
            this.vegaSpec.axes[1].title = `${this.options.yAxisLabel} (${this.options.yUnitOfMeasurement})`;

            this.vegaSpec.marks[3].encode.enter.stroke.value = this.options.lineColor;

            if (!this.options.hideTooltip) {
                this.vegaSpec.marks[4].encode.enter.tooltip.signal = this.options.tooltipSignal || this.tooltipSignal;
            }

            const maxValue = Math.max(...this.graphData.map(value => Math.max(...Object.values(value))));
            const minValue = Math.min(...this.graphData.map(value => Math.min(value.min, value.max, value.y)));

            this.vegaSpec.scales[1].domain = [minValue < 0 ? minValue : 0, maxValue * 1.05];

            this.render();
        },

        /**
         * Format data and add to vegaSpec.
         */
        prepareDataForChart() {
            this.tooltipData = [];

            this.graphData.forEach((dataPoint) => {
                this.tooltipData.push({
                    y: this.prepareNumberForTooltip(dataPoint.y),
                    x: dataPoint.x,
                });
            });

            this.vegaSpec.data[0].values = this.graphData;
            this.vegaSpec.data[1].values = this.tooltipData;
        },
    },
};
</script>

<style lang="scss">
.line-with-area-chart {
    $color-chart-axis-grid: #ddd;
    $color-chart-axis-labels: #000;
    $color-chart-axis-domain: #888;
    $family-primary: 'Lato', sans-serif;

    g.role-axis {
        .role-axis-domain,
        .role-axis-tick {
            line {
                stroke: $color-chart-axis-domain;
            }
        }

        .role-axis-label,
        .role-axis-title {
            text {
                fill: $color-chart-axis-labels;
                font-family: $family-primary;
            }
        }

        .role-axis-grid {
            line {
                stroke: $color-chart-axis-grid;
            }
        }
    }

    .vis-wrapper {
        overflow: hidden;
        width: 100%;
        height: 100%;

        .chart-display-div {
            height: 100%;
            width: 100%;
        }
    }
}
</style>
